import { ApplicationConfig, importProvidersFrom, isDevMode, LOCALE_ID } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import { provideRouter, withAutoCleanupInjectors } from '@angular/router';
import { provideTransloco } from '@jsverse/transloco';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { TranslocoStatiqueLoader } from './core/i18n';
import { CLOCK_SERVICE } from '@shared/services/clock.port';
import { ID_GENERATOR } from '@shared/services/id-generator.port';
import { RealIdGeneratorService } from '@shared/services/real-id-generator.service';
import { CLIPBOARD } from '@shared/clipboard';
import { provideDrawerNavigation } from '@shared/drawer-navigation';
import { SECTION_RIGHTS_SERVICE } from '@shared/services/rights';
import { SectionRightsBackstageService } from './core/rights/section-rights-backstage.service';
import { FEATURE_FLAG_SERVICE } from '@shared/services/feature-flags';
import { FeatureFlagsBackstageAdapter } from './core/config/feature-flags-backstage.adapter';
import { ENVIRONMENT } from './core/config/environment.token';
import { ENVIRONNEMENT_BACKSTAGE } from './core/config/environment-backstage.adapter';
import { RealClipboardService } from '@shared/clipboard/real-clipboard.service';
import { TIMER_SCHEDULER } from '@shared/timing';
import { RealTimerSchedulerService } from '@shared/timing/real-timer-scheduler.service';
import { RealClockService } from '@shared/services/real-clock.service';
import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { MsalBroadcastService, MsalGuard, MsalInterceptor, MsalModule } from '@azure/msal-angular';

import { InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { HttpIntercepteur } from './partage/Intercepteurs/HttpIntercepteur/HttpIntercepteur';

/* L'application tournait en `en-US`, la valeur par defaut d'Angular : aucun
   `LOCALE_ID` n'etait fourni et les donnees de locale francaises n'etaient pas
   enregistrees. Invisible tant que BackStage n'utilisait que des formats de date
   NUMERIQUES (`dd/MM/yyyy`), qui ne dependent pas de la locale — jusqu'a ce que
   la cellule de date du kit (`app-table-date-cell`, format `dd MMM yyyy`) rende
   « 08 Aug 2024 » dans la colonne « Réglé le » du rapprochement bancaire.

   L'enregistrement doit precéder tout rendu, d'ou l'appel au chargement du module. */
registerLocaleData(localeFr);

const protectedResourceMap = new Map<string, string[]>();
protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);
protectedResourceMap.set(environment.backendUrl, environment.azureAd.apiScopes);

export const appConfig: ApplicationConfig = {
  providers: [
    /* Formats de date, de nombre et de pourcentage en francais pour tous les
       pipes d'`@angular/common`. Effet visible au-dela des dates : `| percent`
       passe de « 50% » a « 50 % » (espace insecable avant le signe), qui est la
       typographie francaise correcte. */
    { provide: LOCALE_ID, useValue: 'fr' },

    provideHttpClient(withXhr(), withInterceptorsFromDi()),

    // L'injecteur d'une route est detruit quand on la quitte : l'ecran repart de zero au retour
    provideRouter(routes, withAutoCleanupInjectors()),
    importProvidersFrom(
      MsalModule.forRoot(
        new PublicClientApplication({
          auth: {
            clientId: environment.azureAd.cliendId,
            authority: `https://login.microsoftonline.com/${environment.azureAd.tenantId}`,
            redirectUri: environment.azureAd.redirectUri,
            //postLogoutRedirectUri: '',
          },
          cache: {
            cacheLocation: 'localStorage',
            storeAuthStateInCookie: false,
          },
        }),
        {
          interactionType: InteractionType.Redirect,
        },
        {
          interactionType: InteractionType.Redirect,
          protectedResourceMap: protectedResourceMap,
        },
      ),
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpIntercepteur,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    MsalGuard,

    MsalBroadcastService,
    /* Ports du kit : aucun n'a de fournisseur par défaut, c'est à l'application
       de les câbler. `ID_GENERATOR` et `TIMER_SCHEDULER` sont requis par le
       système de toasts, `CLOCK_SERVICE` par lui et par les composants de date. */
    { provide: CLOCK_SERVICE, useClass: RealClockService },
    { provide: ID_GENERATOR, useClass: RealIdGeneratorService },
    { provide: TIMER_SCHEDULER, useClass: RealTimerSchedulerService },
    { provide: CLIPBOARD, useClass: RealClipboardService },
    { provide: SECTION_RIGHTS_SERVICE, useClass: SectionRightsBackstageService },
    { provide: FEATURE_FLAG_SERVICE, useClass: FeatureFlagsBackstageAdapter },
    { provide: ENVIRONMENT, useValue: ENVIRONNEMENT_BACKSTAGE },
    /* Le conteneur de toasts du kit depend du `DrawerStackStore`, lui-meme adosse
       au registre des tiroirs : la chaine doit etre fournie meme si BackStage
       n'utilise pas les tiroirs. */
    ...provideDrawerNavigation(),
    provideAnimationsAsync(),
    /* Requis par les composants du design system portés depuis POC-MANAGER.
       BackStage est monolingue : `fr` seul, pas de sélecteur de langue. */
    provideTransloco({
      config: {
        availableLangs: ['fr'],
        defaultLang: 'fr',
        reRenderOnLangChange: false,
        prodMode: !isDevMode(),
      },
      loader: TranslocoStatiqueLoader,
    }),
    /* Plusieurs libellés du kit sont écrits en ICU (« {count, plural, … } ») :
       sans ce moteur, transloco renvoie la chaîne ICU telle quelle et l'écran
       affiche la syntaxe brute. La source le fournit aussi. */
    provideTranslocoMessageformat(),
  ],
};
